// models/Training.js
class Training {
  constructor(data = {}) {
    // IDs
    this.id = data.id || data._id || null;
    this._id = data._id || data.id || null;
    this.trainingId = data.trainingId || '';
    
    // Training Details
    this.trainingName = data.trainingName || '';
    this.trainingType = data.trainingType || 'Technical';
    this.trainer = data.trainer || '';
    this.trainerEmail = data.trainerEmail || '';
    this.description = data.description || '';
    
    // Schedule
    this.startDate = data.startDate || '';
    this.endDate = data.endDate || '';
    this.duration = data.duration || '';
    
    // Location & Mode
    this.location = data.location || '';
    this.mode = data.mode || 'Offline';
    
    // Google Meet Integration
    this.meetingLink = data.meetingLink || null;
    this.meetingId = data.meetingId || null;
    this.meetingPassword = data.meetingPassword || null;
    this.calendarEventId = data.calendarEventId || null;
    
    // Capacity & Enrollment
    this.capacity = data.capacity || 0;
    this.enrolled = data.enrolled || 0;
    
    // Participants
    this.participants = data.participants || [];
    
    // Cost
    this.cost = data.cost || 0;
    
    // Status
    this.status = data.status || 'Planned';
    
    // Materials
    this.materials = data.materials || [];
    
    // Feedback & Ratings
    this.feedbacks = data.feedbacks || [];
    this.averageRating = data.averageRating || 0;
    
    // Additional Details
    this.objectives = data.objectives || [];
    this.prerequisites = data.prerequisites || [];
    this.tags = data.tags || [];
    
    // System Fields
    this.createdBy = data.createdBy || null;
    this.updatedBy = data.updatedBy || null;
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
  }

  /**
   * Get status color based on training status
   * @returns {string} - Color hex code
   */
  getStatusColor() {
    const colors = {
      'Planned': '#f59e0b',
      'Ongoing': '#3b82f6',
      'Completed': '#10b981',
      'Cancelled': '#ef4444'
    };
    return colors[this.status] || '#6b7280';
  }

  /**
   * Get available seats
   * @returns {number} - Number of available seats
   */
  getAvailableSeats() {
    return this.capacity - this.enrolled;
  }

  /**
   * Check if training is full
   * @returns {boolean} - True if training is full
   */
  isFull() {
    return this.enrolled >= this.capacity;
  }

  /**
   * Get enrollment percentage
   * @returns {number} - Enrollment percentage (0-100)
   */
  getEnrollmentPercentage() {
    if (this.capacity === 0) return 0;
    return Math.round((this.enrolled / this.capacity) * 100);
  }

  /**
   * Get days remaining until training starts
   * @returns {number|null} - Days remaining or null
   */
  getDaysRemaining() {
    if (!this.startDate) return null;
    const now = new Date();
    const start = new Date(this.startDate);
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }

  /**
   * Check if training is in the past
   * @returns {boolean} - True if training has ended
   */
  isPast() {
    if (!this.endDate) return false;
    return new Date(this.endDate) < new Date();
  }

  /**
   * Check if training is upcoming
   * @returns {boolean} - True if training hasn't started yet
   */
  isUpcoming() {
    if (!this.startDate) return false;
    return new Date(this.startDate) > new Date();
  }

  /**
   * Check if training is currently ongoing
   * @returns {boolean} - True if training is in progress
   */
  isOngoing() {
    if (!this.startDate || !this.endDate) return false;
    const now = new Date();
    return now >= new Date(this.startDate) && now <= new Date(this.endDate);
  }

  /**
   * Check if training is online or hybrid (has meeting capabilities)
   * @returns {boolean} - True if online/hybrid
   */
  hasOnlineMeeting() {
    return this.mode === 'Online' || this.mode === 'Hybrid';
  }

  /**
   * Get meeting room ID for WebRTC
   * @returns {string} - Room ID for video conferencing
   */
  getMeetingRoomId() {
    return this.meetingId || `room-${this.id}`;
  }

  /**
   * Validate training data
   * @returns {Object} - { isValid: boolean, errors: string[] }
   */
  validate() {
    const errors = [];
    
    // Required fields
    if (!this.trainingName?.trim()) {
      errors.push('Training name is required');
    }
    
    if (!this.trainer?.trim()) {
      errors.push('Trainer name is required');
    }
    
    if (!this.startDate) {
      errors.push('Start date is required');
    }
    
    if (!this.endDate) {
      errors.push('End date is required');
    }
    
    // Date validation
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);
      
      if (end < start) {
        errors.push('End date must be after or equal to start date');
      }
    }
    
    // Capacity validation
    if (this.capacity <= 0) {
      errors.push('Capacity must be greater than 0');
    }
    
    // Cost validation
    if (this.cost < 0) {
      errors.push('Cost cannot be negative');
    }
    
    // Enrollment validation
    if (this.enrolled < 0) {
      errors.push('Enrolled count cannot be negative');
    }
    
    if (this.enrolled > this.capacity) {
      errors.push('Enrolled count cannot exceed capacity');
    }
    
    // Email validation (if provided)
    if (this.trainerEmail && !this.isValidEmail(this.trainerEmail)) {
      errors.push('Invalid trainer email format');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - True if valid
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Convert training to JSON for API requests
   * @returns {Object} - Plain object
   */
  toJSON() {
    return {
      trainingName: this.trainingName,
      trainingType: this.trainingType,
      trainer: this.trainer,
      trainerEmail: this.trainerEmail,
      description: this.description,
      startDate: this.startDate,
      endDate: this.endDate,
      duration: this.duration,
      location: this.location,
      mode: this.mode,
      capacity: this.capacity,
      cost: this.cost,
      status: this.status,
      objectives: this.objectives,
      prerequisites: this.prerequisites,
      tags: this.tags
    };
  }

  /**
   * Create Training instance from JSON response
   * @param {Object} json - JSON data from API
   * @returns {Training} - Training instance
   */
  static fromJSON(json) {
    return new Training(json);
  }

  /**
   * Get formatted date string
   * @param {string} date - ISO date string
   * @param {string} format - 'short' | 'long' | 'time'
   * @returns {string} - Formatted date
   */
  static formatDate(date, format = 'short') {
    if (!date) return 'N/A';
    
    const d = new Date(date);
    
    switch (format) {
      case 'short':
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      
      case 'long':
        return d.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long'
        });
      
      case 'time':
        return d.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      
      default:
        return d.toLocaleDateString('en-US');
    }
  }

  /**
   * Get mode icon name
   * @returns {string} - Icon name for the mode
   */
  getModeIcon() {
    const icons = {
      'Online': 'Video',
      'Offline': 'MapPin',
      'Hybrid': 'Monitor'
    };
    return icons[this.mode] || 'MapPin';
  }

  /**
   * Get status badge style
   * @returns {Object} - Style object for status badge
   */
  getStatusBadgeStyle() {
    const color = this.getStatusColor();
    return {
      backgroundColor: `${color}20`,
      color: color
    };
  }

  /**
   * Get type badge color
   * @returns {string} - Color hex code
   */
  getTypeBadgeColor() {
    const colors = {
      'Technical': '#3b82f6',
      'Soft Skills': '#8b5cf6',
      'Leadership': '#f59e0b',
      'Compliance': '#ef4444',
      'Safety': '#10b981',
      'Product': '#06b6d4',
      'Other': '#6b7280'
    };
    return colors[this.trainingType] || '#6b7280';
  }

  /**
   * Check if user can enroll
   * @returns {boolean} - True if enrollment is possible
   */
  canEnroll() {
    return (
      !this.isFull() &&
      this.status === 'Planned' &&
      this.isUpcoming()
    );
  }

  /**
   * Get enrollment status message
   * @returns {string} - Status message
   */
  getEnrollmentStatus() {
    if (this.isFull()) return 'Full';
    if (this.status === 'Completed') return 'Completed';
    if (this.status === 'Cancelled') return 'Cancelled';
    if (this.isPast()) return 'Past';
    
    const available = this.getAvailableSeats();
    return `${available} seat${available !== 1 ? 's' : ''} available`;
  }
}

export default Training;